import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { get, request } from '../../lib/apiClient';
import { planClass } from './planBadge';
import styles from './Admin.module.css';

/**
 * Admin → Plan limits (TOADO-507).
 *
 * Each plan's recording limits, edited here rather than in code so they can be
 * tuned against what recording costs without a deploy. Beside each plan: the
 * most one workspace can cost at these limits, and what the last thirty days
 * actually cost, so a change is made with both in view.
 *
 * Server side: GET /admin/plan-limits, PUT /admin/plan-limits/:plan (refused
 * past a ceiling, audited as admin.plan_limits_set), and a retention preview,
 * because shortening retention deletes video at the next nightly pass and is
 * the one change here that cannot be undone.
 */
type Plan = 'free' | 'personal' | 'team' | 'business' | 'enterprise';

interface PlanRow {
  plan: Plan;
  maxRecordingSeconds: number;
  recordingMinutesMonthly: number | null;
  transcriptionMinutesMonthly: number | null;
  storageGbCap: number | null;
  videoRetentionDays: number;
  /** The widget's allowances (TOADO-661): per workspace, not per seat; null is unlimited. */
  visitorMinutesMonthly: number | null;
  widgetReportsMonthly: number | null;
  updatedAt: string | null;
  updatedBy: string | null;
  cost: {
    worstCaseCentsPerWorkspace: { transcription: number | null; storage: number | null };
    last30Days: {
      workspaces: number;
      transcribedMinutes: number;
      transcriptionCents: number;
      topWorkspaceTranscribedMinutes: number;
      recordingBytesHeld: number;
      topWorkspaceBytesHeld: number;
    };
  };
}

interface PlanLimitsResponse {
  audience: 'invited' | 'everyone';
  ceilings: {
    maxRecordingSeconds: { min: number; max: number };
    videoRetentionDays: { min: number; max: number };
    minutesMax: number;
    reportsMax: number;
    storageGbMax: number;
  };
  plans: PlanRow[];
}

interface RetentionPreview {
  clips: number;
  walkthroughs: number;
  workspaces: number;
  bytes: number;
}

export function PlanLimitsPage() {
  const { data, isLoading, error } = useQuery({
    queryKey: ['admin', 'plan-limits'],
    queryFn: () => get<PlanLimitsResponse>('/admin/plan-limits'),
    staleTime: 30_000,
  });

  return (
    <>
      <h1 className={styles.h1}>Plan limits</h1>
      <p className={styles.sub}>
        How long a walkthrough may run, how many minutes a month a workspace may record and have
        transcribed, how much video it may hold, and how long the video is kept. Team, Business and
        Enterprise values are per seat, pooled across the workspace; an empty minutes, reports or
        storage field is unlimited. A workspace's own override on the Overrides page wins over its
        plan. Changes apply within a minute, to new recordings only: one already running may finish.
      </p>
      <p className={styles.sub}>
        Visitor minutes (visitors' recordings through the widget) and widget reports are per
        workspace, not per seat, and a widget tier adds its own on top. Business includes the
        Starter tier, so its own figures (0 by default) come on top of Starter's. Free's widget
        reports are held by its ticket cap.
      </p>
      {data && (
        <p className={styles.sub}>
          Recording is open to{' '}
          <strong>{data.audience === 'everyone'
            ? 'every workspace not switched off'
            : 'staff and the workspaces switched on'}</strong>
          {data.audience === 'everyone'
            ? '.'
            : '. Set FEATURE_AUDIENCE=everyone on the API to open it to every workspace, new ones included.'}
        </p>
      )}
      <div className={styles.section}>
        {isLoading ? (
          <p className={styles.muted}>Loading…</p>
        ) : error || !data ? (
          <p className={styles.muted}>The plan limits could not be loaded.</p>
        ) : (
          <table className={styles.table}>
            <thead>
              <tr>
                <th>Plan</th>
                <th>Longest recording</th>
                <th>Recording min / month</th>
                <th>Transcribed min / month</th>
                <th>Storage (GB)</th>
                <th>Video kept (days)</th>
                <th>Visitor min / month</th>
                <th>Widget reports / month</th>
                <th>Worst case / workspace</th>
                <th>Last 30 days</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {data.plans.map((row) => <PlanLimitRow key={row.plan} row={row} ceilings={data.ceilings} />)}
            </tbody>
          </table>
        )}
      </div>
    </>
  );
}

type Draft = {
  maxRecordingMinutes: string;
  recordingMinutesMonthly: string;
  transcriptionMinutesMonthly: string;
  storageGbCap: string;
  videoRetentionDays: string;
  visitorMinutesMonthly: string;
  widgetReportsMonthly: string;
};

function draftFrom(row: PlanRow): Draft {
  const text = (value: number | null) => (value === null ? '' : String(value));
  return {
    maxRecordingMinutes: String(row.maxRecordingSeconds / 60),
    recordingMinutesMonthly: text(row.recordingMinutesMonthly),
    transcriptionMinutesMonthly: text(row.transcriptionMinutesMonthly),
    storageGbCap: text(row.storageGbCap),
    videoRetentionDays: String(row.videoRetentionDays),
    visitorMinutesMonthly: text(row.visitorMinutesMonthly),
    widgetReportsMonthly: text(row.widgetReportsMonthly),
  };
}

function PlanLimitRow({ row, ceilings }: { row: PlanRow; ceilings: PlanLimitsResponse['ceilings'] }) {
  const qc = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<Draft>(() => draftFrom(row));
  const [reason, setReason] = useState('');
  const [confirmedDeletion, setConfirmedDeletion] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  useEffect(() => { if (!editing) setDraft(draftFrom(row)); }, [editing, row]);

  const retentionDays = Number(draft.videoRetentionDays);
  const shortens = editing && Number.isInteger(retentionDays) && retentionDays > 0 && retentionDays < row.videoRetentionDays;
  const preview = useQuery({
    queryKey: ['admin', 'plan-limits', row.plan, 'retention-preview', retentionDays],
    queryFn: () => get<RetentionPreview>(`/admin/plan-limits/${row.plan}/retention-preview?days=${retentionDays}`),
    enabled: shortens,
  });
  const deletes = shortens && (preview.data?.clips ?? 0) > 0;

  const save = useMutation({
    mutationFn: () => {
      const count = (value: string) => (value.trim() === '' ? null : Number(value));
      return request(`/admin/plan-limits/${row.plan}`, {
        method: 'PUT',
        body: JSON.stringify({
          maxRecordingSeconds: Math.round(Number(draft.maxRecordingMinutes) * 60),
          recordingMinutesMonthly: count(draft.recordingMinutesMonthly),
          transcriptionMinutesMonthly: count(draft.transcriptionMinutesMonthly),
          storageGbCap: count(draft.storageGbCap),
          videoRetentionDays: Number(draft.videoRetentionDays),
          visitorMinutesMonthly: count(draft.visitorMinutesMonthly),
          widgetReportsMonthly: count(draft.widgetReportsMonthly),
          reason: reason.trim(),
        }),
      });
    },
    onSuccess: () => {
      setEditing(false);
      setReason('');
      setConfirmedDeletion(false);
      setMessage(`Saved. New ${row.plan} recordings use these limits within a minute.`);
      void qc.invalidateQueries({ queryKey: ['admin', 'plan-limits'] });
      void qc.invalidateQueries({ queryKey: ['admin', 'overrides', 'history'] });
    },
    onError: (err: unknown) => {
      setMessage(err instanceof Error ? err.message : 'The limits could not be saved.');
    },
  });

  // Free's widget reports are held by its ticket cap, so its own figure does nothing.
  const reportsByTicketCap = row.plan === 'free';
  const field = (key: keyof Draft, label: string, props: { min: number; max: number; step?: number; allowEmpty?: boolean; disabled?: boolean }) => (
    <label style={{ display: 'grid', gap: 4, fontSize: 11, color: 'var(--text-tertiary)' }}>
      {label}
      <input
        type="number"
        min={props.min}
        max={props.max}
        step={props.step ?? 1}
        value={draft[key]}
        placeholder={props.allowEmpty ? 'Unlimited' : undefined}
        disabled={props.disabled}
        onChange={(event) => setDraft((current) => ({ ...current, [key]: event.target.value }))}
        style={inputStyle}
      />
    </label>
  );

  const cost = row.cost;
  return (
    <>
      <tr>
        <td><span className={`${styles.planBadge} ${planClass(row.plan)}`}>{row.plan}</span></td>
        <td>{row.maxRecordingSeconds / 60} min</td>
        <td>{limitText(row.recordingMinutesMonthly)}</td>
        <td>{limitText(row.transcriptionMinutesMonthly)}</td>
        <td>{limitText(row.storageGbCap)}</td>
        <td>{row.videoRetentionDays}</td>
        <td>{limitText(row.visitorMinutesMonthly)}</td>
        <td>
          {reportsByTicketCap
            ? <span className={styles.muted} title="Free's widget reports count against its monthly ticket cap">Ticket cap</span>
            : limitText(row.widgetReportsMonthly)}
        </td>
        <td className={styles.muted}>
          {cents(cost.worstCaseCentsPerWorkspace.transcription)} transcription
          <br />
          {cents(cost.worstCaseCentsPerWorkspace.storage)} storage
        </td>
        <td className={styles.muted}>
          {cost.last30Days.transcribedMinutes} min transcribed ({cents(cost.last30Days.transcriptionCents)})
          <br />
          top workspace {cost.last30Days.topWorkspaceTranscribedMinutes} min, {megabytes(cost.last30Days.topWorkspaceBytesHeld)} held
        </td>
        <td>
          <button type="button" className={styles.expandBtn} onClick={() => { setEditing((v) => !v); setMessage(null); }}>
            {editing ? 'Cancel' : 'Edit'}
          </button>
        </td>
      </tr>
      {(editing || message) && (
        <tr className={styles.expandRow}>
          <td colSpan={11}>
            {editing && (
              <div style={{ display: 'grid', gap: 12 }}>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 12 }}>
                  {field('maxRecordingMinutes', 'Longest recording (minutes)', {
                    min: ceilings.maxRecordingSeconds.min / 60, max: ceilings.maxRecordingSeconds.max / 60,
                  })}
                  {field('recordingMinutesMonthly', 'Recording minutes a month', { min: 0, max: ceilings.minutesMax, allowEmpty: true })}
                  {field('transcriptionMinutesMonthly', 'Transcribed minutes a month', { min: 0, max: ceilings.minutesMax, allowEmpty: true })}
                  {field('storageGbCap', 'Storage (GB)', { min: 0, max: ceilings.storageGbMax, allowEmpty: true })}
                  {field('videoRetentionDays', 'Video kept (days)', ceilings.videoRetentionDays)}
                  {field('visitorMinutesMonthly', 'Visitor minutes a month', { min: 0, max: ceilings.minutesMax, allowEmpty: true })}
                  {field('widgetReportsMonthly', 'Widget reports a month', {
                    min: 0, max: ceilings.reportsMax, allowEmpty: true, disabled: reportsByTicketCap,
                  })}
                </div>
                {reportsByTicketCap && (
                  <p className={styles.muted} style={{ margin: 0, fontSize: 12 }}>
                    Free's widget reports are held by its monthly ticket cap, so they cannot be set here.
                  </p>
                )}
                {shortens && (
                  <p style={{ margin: 0, fontSize: 12, color: 'var(--warning, #b45309)' }}>
                    {preview.isLoading
                      ? 'Counting the video this would delete…'
                      : deletes
                        ? `Saving deletes ${preview.data!.clips} clips (${megabytes(preview.data!.bytes)}) from ${preview.data!.walkthroughs} walkthroughs in ${preview.data!.workspaces} workspaces at the next nightly pass. Their tickets, screenshots and transcripts stay.`
                        : 'No video on this plan is old enough to be deleted by this change.'}
                  </p>
                )}
                {deletes && (
                  <label style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 12 }}>
                    <input type="checkbox" checked={confirmedDeletion} onChange={(event) => setConfirmedDeletion(event.target.checked)} />
                    I understand this video cannot be recovered.
                  </label>
                )}
                <div style={{ display: 'flex', gap: 8 }}>
                  <input
                    type="text"
                    value={reason}
                    onChange={(event) => setReason(event.target.value)}
                    placeholder="Reason (required)"
                    style={{ ...inputStyle, flex: 1 }}
                  />
                  <button
                    type="button"
                    className={styles.expandBtn}
                    disabled={save.isPending || reason.trim() === '' || (deletes && !confirmedDeletion) || (shortens && preview.isLoading)}
                    onClick={() => save.mutate()}
                  >
                    Save {row.plan}
                  </button>
                </div>
              </div>
            )}
            {message && <p className={styles.muted} role="status" style={{ margin: editing ? '8px 0 0' : 0 }}>{message}</p>}
          </td>
        </tr>
      )}
    </>
  );
}

function limitText(value: number | null): string {
  return value === null ? 'Unlimited' : value.toLocaleString();
}

function cents(value: number | null): string {
  if (value === null) return 'Unbounded';
  return `$${(value / 100).toFixed(2)}`;
}

function megabytes(bytes: number): string {
  if (bytes >= 1024 ** 3) return `${(bytes / 1024 ** 3).toFixed(1)} GB`;
  return `${Math.round(bytes / 1024 ** 2)} MB`;
}

const inputStyle: React.CSSProperties = {
  padding: '6px 10px',
  fontSize: 12,
  fontFamily: 'inherit',
  border: '1px solid var(--border)',
  borderRadius: 4,
  background: 'var(--surface)',
  color: 'inherit',
};
